import {
  CompositeFilterDescriptor,
  FilterDescriptor,
  State,
} from '@progress/kendo-data-query';
import { Catalog } from 'src/types/catalog';
import { MarketplaceCategoriesResponse } from 'src/types/view_catalogs/MarketplaceCategories';

export interface SupplierOption {
  field: string;
  title: string;
  show: boolean;
  display: boolean;
}

// Supplier BASED ON catalogType TYPE
export const SUPPLIER_CATALOG_TYPE_MAP: Record<string, number> = {
  hosted: 1,
  offline: 3,
  punchout: 2,
};

export const isOfflineCatalog = (catalogType?: number): boolean =>
  catalogType === SUPPLIER_CATALOG_TYPE_MAP.offline;

// This one is used to check if all suppliers are selected or not if selected show everything
export function areAllSuppliersSelected(selectedSupplier: SupplierOption[]): boolean {
  return selectedSupplier
    .filter((supplier) => supplier.display)
    .every((supplier) => supplier.show);
}

// Returns API numbers. Example: Hosted + Punchout checked → [1, 2]
function getSelectedCatalogTypes(selectedSupplier: SupplierOption[]): number[] {
  return selectedSupplier
    .filter((supplier) => supplier.show)
    .map((supplier) => SUPPLIER_CATALOG_TYPE_MAP[supplier.field]);
}

// Returns category options based on the selected suppliers
export function getCategoryOptionsForSuppliers(
  catalogs: Catalog[],
  marketplaceCategories: MarketplaceCategoriesResponse[],
  selectedSupplier: SupplierOption[]
): MarketplaceCategoriesResponse[] {
  if (areAllSuppliersSelected(selectedSupplier)) {
    return marketplaceCategories;
  }

  const selectedCatalogTypes = getSelectedCatalogTypes(selectedSupplier);
  const categoryIds = new Set<number>();

  catalogs
    .filter((catalog) => selectedCatalogTypes.includes(catalog.catalogType))
    .forEach((catalog) => {
      catalog.categories?.forEach((categoryId) => categoryIds.add(categoryId));
    });

  return marketplaceCategories.filter((category) => categoryIds.has(category.categoryId));
}

// Builds the filter for the catalog grid. 
export function buildBrowseCatalogsFilter(
  selectedSupplier: SupplierOption[],
  selectedCategory: MarketplaceCategoriesResponse | null,
  searchText = ''
): CompositeFilterDescriptor {
  const filters: Array<FilterDescriptor | CompositeFilterDescriptor> = [];

  if (!areAllSuppliersSelected(selectedSupplier)) {
    const selectedCatalogTypes = getSelectedCatalogTypes(selectedSupplier);
    filters.push({
      logic: 'or',
      filters: selectedCatalogTypes.map((catalogType) => ({
        field: 'catalogType',
        operator: 'eq',
        value: catalogType,
      })),
    });
  }

  if (selectedCategory?.categoryId) {
    filters.push({
      field: 'categories',
      operator: 'contains',
      value: selectedCategory.categoryId,
    });
  }

  const term = searchText.trim();
  if (term) {
    filters.push({
      logic: 'or',
      filters: [
        { field: 'catalogName', operator: 'contains', value: term, ignoreCase: true },
        { field: 'accountNumber', operator: 'contains', value: term, ignoreCase: true },
      ],
    });
  }

  return { logic: 'and', filters };
}

export const DEFAULT_BROWSE_CATALOGS_DATA_STATE: State = {
  sort: [{ field: 'catalogName', dir: 'asc' }],
  filter: { logic: 'and', filters: [] },
};

export const DEFAULT_SELECTED_CATEGORY: MarketplaceCategoriesResponse = {
  categoryId: 0,
  description: 'All Categories',
};


export function getCategoryDropdownOptions(
  categoryOptions: MarketplaceCategoriesResponse[],
  allCategoriesLabel: string
): MarketplaceCategoriesResponse[] {
  return [
    { ...DEFAULT_SELECTED_CATEGORY, description: allCategoriesLabel },
    ...categoryOptions.filter((category) => category.categoryId !== 0),
  ];
}

export function resolveSelectedCategory(
  selectedCategory: MarketplaceCategoriesResponse | null,
  categoryOptions: MarketplaceCategoriesResponse[],
  allCategoriesLabel: string
): MarketplaceCategoriesResponse {
  const allCategoriesOption = { ...DEFAULT_SELECTED_CATEGORY, description: allCategoriesLabel };

  if (!selectedCategory?.categoryId) {
    return allCategoriesOption;
  }

  return (
    categoryOptions.find((category) => category.categoryId === selectedCategory.categoryId) ??
    allCategoriesOption
  );
}
